/**
 * @file Pure builder of antd grouped `Select` options for every Typesafe-judged
 * mapping axis in the review UI (fields, columns, tables, reference facts). Given
 * the model's scored picks (winner + runners-up, intersected with the live option
 * universe by the caller so stale scores drop after a retarget) and the flat
 * universe, it returns a two-tier grouping: a "Top matches" tier carrying each
 * ranked value with a leading `NN% ·` prefix from `mappingReviewConfidence` (same
 * rounding as the ConfidenceTag) — the score sits ahead of the label so an
 * `overflow:clip` Select can never truncate it off the end — descending, deduped
 * (winner first), capped at
 * `MAPPING_ALTERNATIVES_TOP_K`, and a universe tier holding everything else —
 * omitted when every universe entry ranked, so no empty group header renders. The
 * `disabled` flag passes through BOTH tiers (disable, never hide). With no ranked
 * entries it returns the flat universe unchanged. Consumed by the per-axis
 * re-picker Selects in `MappingReviewFields`, `MappingSelect` (column
 * targets), and `MappingReviewTables`.
 */
import { MAPPING_ALTERNATIVES_TOP_K } from '#types';
import { mappingReviewConfidence } from './mappingReviewConfidence';

/** One flat option in an axis's full universe. */
export interface RankedOptionEntry<V extends string | number> {
  value: V;
  label: string;
  disabled?: boolean;
}

/** One Typesafe-scored pick for an axis (winner or runner-up), pre-intersected with the universe. */
export interface RankedScore<V extends string | number> {
  value: V;
  probability: number;
}

export interface RankedOptionGroup<V extends string | number> {
  label: string;
  options: RankedOptionEntry<V>[];
}

/**
 * Either a flat universe (no ranked entries survived) or a two-tier grouping.
 * A single union array so antd's `Select` `options` prop accepts it directly —
 * both members carry `label`, group members add nested `options`.
 */
export type RankedOptions<V extends string | number> = (RankedOptionEntry<V> | RankedOptionGroup<V>)[];

const TOP_MATCHES_LABEL = 'Top matches';

export function mappingReviewRankedOptions<V extends string | number>(args: {
  scored: RankedScore<V>[];
  universe: RankedOptionEntry<V>[];
  universeLabel: string;
}): RankedOptions<V> {
  const { scored, universe, universeLabel } = args;
  const byValue = new Map(universe.map((option) => [option.value, option]));

  const ranked: RankedOptionEntry<V>[] = [];
  const seen = new Set<V>();
  for (const { value, probability } of [...scored].sort((a, b) => b.probability - a.probability)) {
    if (seen.has(value)) continue;
    const option = byValue.get(value);
    if (!option) continue;
    seen.add(value);
    ranked.push({ ...option, label: `${mappingReviewConfidence(probability).label} · ${option.label}` });
    if (ranked.length >= MAPPING_ALTERNATIVES_TOP_K) break;
  }

  if (ranked.length === 0) return universe;

  const rest = universe.filter((option) => !seen.has(option.value));
  if (rest.length === 0) return [{ label: TOP_MATCHES_LABEL, options: ranked }];
  return [
    { label: TOP_MATCHES_LABEL, options: ranked },
    { label: universeLabel, options: rest },
  ];
}
